import React from 'react';
import { 
  FileText, 
  Download, 
  Image, 
  Film, 
  Music, 
  Archive, 
  File,
  FileSpreadsheet,
  FileCode
} from 'lucide-react';
import { V1SectionContainer } from './V1SectionContainer';

interface Attachment {
  id: string;
  name?: string;
  description?: string;
  position?: number;
  fileUrl?: string;
  fileFileName?: string;
  fileContentType?: string;
  fileFileSize?: number;
  fileUpdatedAt?: string;
  formattedFileSize?: string;
  fileType?: string;
  isImage?: boolean;
  imageThumbUrl?: string;
  imageMediumUrl?: string;
  imageLargeUrl?: string;
  showAt?: string;
  isVisible?: boolean;
  downloadsCount?: number;
  uniqueDownloadsCount?: number;
}

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  attachments?: Attachment[];
  sectionOption?: any;
}

interface V1FilesSectionProps {
  section: Section;
}

/**
 * V1 Files Section - FROZEN
 *
 * Displays downloadable files with file type icons.
 * Shows file name, size, and download button.
 *
 * DO NOT add V2 features to this component.
 */
export const V1FilesSection: React.FC<V1FilesSectionProps> = ({ section }) => {
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType?: string) => {
    if (!contentType) return File;
    
    const type = contentType.toLowerCase();
    
    // PDF files - most common for planners
    if (type.includes('pdf')) return FileText;
    
    // Images
    if (type.includes('image')) return Image;
    
    // Word documents
    if (type.includes('word') || type.includes('document')) return FileText;
    
    // Excel/Spreadsheets
    if (type.includes('excel') || type.includes('spreadsheet')) return FileSpreadsheet;
    
    // PowerPoint/Presentations
    if (type.includes('powerpoint') || type.includes('presentation')) return FileText;
    
    // Video files
    if (type.includes('video')) return Film;
    
    // Audio files
    if (type.includes('audio')) return Music;
    
    // Archives (zip, rar, etc)
    if (type.includes('zip') || type.includes('compressed') || type.includes('archive')) return Archive;
    
    // Code/Text files
    if (type.includes('text')) return FileCode;
    
    // Default
    return File;
  };

  const files = section.attachments || [];

  if (files.length === 0) {
    return (
      <V1SectionContainer section={section}>
        {section.customTitle && (
          <h2 className="text-2xl font-bold mb-4">{section.customTitle}</h2>
        )}
        <p className="text-gray-500">No files available for download.</p>
      </V1SectionContainer>
    );
  }

  return (
    <V1SectionContainer section={section}>
      {section.customTitle && (
        <h2 className="text-2xl font-bold mb-6">{section.customTitle}</h2>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {files.map((file) => {
          const Icon = getFileIcon(file.fileContentType);
          // Use name first, then fall back to fileFileName from Shrine
          const fileName = file.name || file.fileFileName || 'Unnamed File';
          // Use the pre-formatted file size from the serializer
          const fileSize = file.formattedFileSize || formatFileSize(file.fileFileSize);

          return (
            <div
              key={file.id}
              className="flex flex-col p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <Icon size={20} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{fileName}</p>
                  <p className="text-sm text-gray-500">{fileSize}</p>
                </div>
              </div>
              {file.description && (
                <p className="text-sm text-gray-600 mb-2">{file.description}</p>
              )}
              {file.fileUrl && (
                <a
                  href={file.fileUrl}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-auto text-blue-600 hover:text-blue-700 text-sm font-medium"
                  title={`Download ${fileName}`}
                >
                  <Download size={16} />
                  Download
                </a>
              )}
            </div>
          );
        })}
      </div>
    </V1SectionContainer>
  );
};